import { HpClientDebugFlags } from '../clientDebugFlags';
import { logError, logInfo } from '../clientLogging';

export interface HpBulkRecordProcessorProps<RecordT> {
  minRecordsToProcess: number;
  maxTimeBetweenProcessing: number;
  processor: (records: RecordT[], timesCalled: number) => Promise<void>;
  canProcess: () => boolean;
}

export interface HpBulkRecordProcessor<RecordT> {
  add: (item: RecordT) => void;
  removeAll: () => RecordT[];
  getLastCheckedTime: () => number | undefined;
  getLastProcessedTime: () => number | undefined;
  stop: VoidFunction;
  getId: () => string;
}

export function createBulkRecordProcessor<RecordT>({
  minRecordsToProcess,
  maxTimeBetweenProcessing,
  processor,
  canProcess
}: HpBulkRecordProcessorProps<RecordT>): HpBulkRecordProcessor<RecordT> {
  const recordStack: RecordT[] = [];
  const processorId = 'BRP#' + Math.floor(1000 + Math.random() * 9000);
  let timerId: NodeJS.Timeout | undefined;
  let lastCheckedTime: number | undefined;
  let lastProcessedTime: number | undefined;
  let timesCalled = 0;
  const log = (msg: string) => logInfo(`[${processorId}] ${msg}`, HpClientDebugFlags.BulkRecordProcessor);

  function removeAll(): RecordT[] {
    const data = [...recordStack];
    recordStack.length = 0;
    return data;
  }

  function shouldProcess(minCount: number): boolean {
    const count = recordStack.length;
    const shouldProc = canProcess() && count >= minCount;
    const procMsg = shouldProc ? 'will process' : "won't process";
    const timerMsg = timerId !== undefined ? `, timerId: ${timerId}` : '';
    log(`Checked (count: ${count}, minCount: ${minCount}${timerMsg}; ${procMsg})`);
    return shouldProc;
  }

  function clearProcessTimeout() {
    if (timerId) {
      clearTimeout(timerId);
      log(`Clear timer [${timerId}]`);
      timerId = undefined;
    }
  }

  function processRecords(): void {
    clearProcessTimeout();
    lastProcessedTime = new Date().getTime();
    const records = removeAll();
    const times = ++timesCalled;
    log(`Processing ${records.length} records (#${times})`);
    processor(records, times)
      .then(() => {
        return;
      })
      .catch((reason) => {
        setDelayedProcess();
        logError(`[${processorId}] ${reason.message || reason}`);
      });
  }

  function setDelayedProcess(onlyIfNotSet?: boolean) {
    if (onlyIfNotSet === true && timerId) {
      return;
    }

    clearProcessTimeout();
    timerId = setTimeout(() => {
      log(`Run timer [${timerId}]`);
      lastCheckedTime = new Date().getTime();
      timerId = undefined;
      if (shouldProcess(1)) {
        processRecords();
      }
    }, maxTimeBetweenProcessing);
    log(`Set timer [${timerId}]`);
  }

  function add(item: RecordT): void {
    recordStack.push(item);
    if (shouldProcess(minRecordsToProcess)) {
      processRecords();
    } else {
      setDelayedProcess(true);
    }
  }

  function getLastCheckedTime(): number | undefined {
    return lastCheckedTime;
  }

  function getLastProcessedTime(): number | undefined {
    return lastProcessedTime;
  }

  function stop(): void {
    if (timerId !== undefined) {
      log(`Halting checks (${timerId})`);
      clearTimeout(timerId);
    }
  }

  function getId(): string {
    return processorId;
  }

  return {
    add,
    removeAll,
    getLastCheckedTime,
    getLastProcessedTime,
    stop,
    getId
  };
}
